/* Local-first storage.
 *
 * Records, not one blob: every space, document and note is its own row with an
 * `updatedAt` stamp and a `deletedAt` tombstone. That is what makes a later sync
 * to Postgres tractable — two devices editing different chapters merge instead of
 * clobbering, and a delete on one device stays deleted on the other.
 *
 * IndexedDB is the source of truth. localStorage is the fallback when IndexedDB
 * is unavailable (private windows, `file://`, ancient browsers) so the app still
 * runs everywhere, just without the headroom.
 */

import { saveBeforeQuit } from './desktop.js';
import { seed } from './seed.js';

export const KINDS = ['spaces', 'documents', 'notes'];

const DB_NAME = 'longhand';
const DB_VERSION = 1;
const FALLBACK_KEY = 'longhand-records-v1';
const LEGACY_KEY = 'longhand-v1';
const WRITE_DEBOUNCE_MS = 400;

/**
 * Monotonic timestamps. `updatedAt` is the ordering key the whole merge rests
 * on, and Date has millisecond resolution — two operations in the same
 * millisecond would compare equal, so an edit made in the same tick as a
 * sync-mark would look already-synced and never be pushed. Never hand back a
 * stamp that is not strictly greater than the last one.
 */
let lastStamp = '';
function now() {
  let s = new Date().toISOString();
  if (s <= lastStamp) s = new Date(Date.parse(lastStamp) + 1).toISOString();
  lastStamp = s;
  return s;
}
const uid = () => (globalThis.crypto?.randomUUID
  ? crypto.randomUUID()
  : 'id-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10));

/* ------------------------------------------------------------- adapters */

function openIDB(name = DB_NAME) {
  return new Promise((resolve, reject) => {
    if (!globalThis.indexedDB) return reject(new Error('no indexedDB'));
    let req;
    try { req = indexedDB.open(name, DB_VERSION); } catch (e) { return reject(e); }
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const k of [...KINDS, 'meta']) {
        if (!db.objectStoreNames.contains(k)) db.createObjectStore(k, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('indexedDB open failed'));
    req.onblocked = () => reject(new Error('indexedDB blocked'));
  });
}

function idbAdapter(db) {
  const run = (names, mode, fn) => new Promise((resolve, reject) => {
    const tx = db.transaction(names, mode);
    const out = fn(tx);
    tx.oncomplete = () => resolve(out);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('transaction aborted'));
  });

  return {
    name: 'indexeddb',
    async readAll() {
      const acc = {};
      await run([...KINDS, 'meta'], 'readonly', tx => {
        for (const k of [...KINDS, 'meta']) {
          const req = tx.objectStore(k).getAll();
          req.onsuccess = () => { acc[k] = req.result; };
        }
      });
      return acc;
    },
    write(batch) {
      const names = Object.keys(batch).filter(k => batch[k].length);
      if (!names.length) return Promise.resolve();
      return run(names, 'readwrite', tx => {
        for (const k of names) for (const rec of batch[k]) tx.objectStore(k).put(rec);
      });
    },
    wipe() {
      return run([...KINDS, 'meta'], 'readwrite', tx => {
        for (const k of [...KINDS, 'meta']) tx.objectStore(k).clear();
      });
    },
    close() { db.close(); }
  };
}

function localAdapter(name = DB_NAME) {
  const storageKey = FALLBACK_KEY + ':' + name;
  const read = () => {
    try { return JSON.parse(localStorage.getItem(storageKey) || 'null') || {}; }
    catch (e) { return {}; }
  };
  const write = d => { try { localStorage.setItem(storageKey, JSON.stringify(d)); } catch (e) {} };

  return {
    name: 'localstorage',
    async readAll() { return read(); },
    async write(batch) {
      const d = read();
      for (const k of Object.keys(batch)) {
        const byId = new Map((d[k] || []).map(r => [r.id, r]));
        for (const rec of batch[k]) byId.set(rec.id, rec);
        d[k] = [...byId.values()];
      }
      write(d);
    },
    async wipe() { write({}); },
    close() {}
  };
}

async function pickAdapter(name) {
  try { return idbAdapter(await openIDB(name)); }
  catch (e) {
    console.warn('Longhand: IndexedDB unavailable, falling back to localStorage —', e.message);
    return localAdapter(name);
  }
}

/**
 * Read a different local database without disturbing this one. Used to adopt
 * writing that predates signing in, which lives in the un-namespaced database.
 */
export async function readDatabase(name) {
  try {
    const adapter = idbAdapter(await openIDB(name));
    const raw = await adapter.readAll();
    adapter.close();
    const out = {};
    for (const k of KINDS) out[k] = raw[k] || [];
    return KINDS.some(k => out[k].length) ? out : null;
  } catch (e) { return null; }
}

/* ------------------------------------------------------------ migration */

/** Flatten the nested authoring/legacy shape into records. */
export function recordsFromNested(data, stamp = now()) {
  const out = { spaces: [], documents: [], notes: [] };
  (data.spaces || []).forEach((sp, si) => {
    out.spaces.push({
      id: sp.id || uid(), name: sp.name || '', kind: sp.kind || '',
      description: sp.description ?? sp.desc ?? '', goal: Number(sp.goal) || 0,
      position: si, updatedAt: sp.updatedAt || stamp, deletedAt: sp.deletedAt || null
    });
    const spaceId = out.spaces[out.spaces.length - 1].id;
    (sp.docs || []).forEach((d, di) => out.documents.push({
      id: d.id || uid(), spaceId, title: d.title || '', synopsis: d.synopsis || '',
      content: d.content || '', position: di,
      updatedAt: d.updatedAt || stamp, deletedAt: d.deletedAt || null
    }));
    (sp.notes || []).forEach((n, ni) => out.notes.push({
      id: n.id || uid(), spaceId, kind: n.kind || 'world', title: n.title || '',
      body: n.body || '', position: ni,
      updatedAt: n.updatedAt || stamp, deletedAt: n.deletedAt || null
    }));
  });
  return out;
}

function readLegacyBlob() {
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    return d && Array.isArray(d.spaces) ? d : null;
  } catch (e) { return null; }
}

/* ------------------------------------------------------------- conflicts */

/* Fields that carry actual writing. Position and timestamps changing is not a
   conflict; a different sentence is. */
const CONTENT_FIELDS = {
  spaces: ['name', 'kind', 'description', 'goal'],
  documents: ['title', 'synopsis', 'content'],
  notes: ['kind', 'title', 'body']
};

/* Only content-bearing records get conflict copies. Duplicating a space would
   orphan its documents, so spaces stay plain last-write-wins. */
const CONFLICTABLE = new Set(['documents', 'notes']);

const differs = (kind, a, b) => CONTENT_FIELDS[kind].some(f => (a[f] ?? '') !== (b[f] ?? ''));
const hasWriting = (kind, r) => CONTENT_FIELDS[kind].some(f => f !== 'kind' && String(r[f] ?? '').trim() !== '');

/* ----------------------------------------------------------- repository */

class Store {
  constructor(adapter) {
    this.adapter = adapter;
    this.rows = { spaces: new Map(), documents: new Map(), notes: new Map() };
    this.meta = new Map();
    this._dirty = { spaces: new Set(), documents: new Set(), notes: new Set(), meta: new Set() };
    this._timer = null;
    this._projection = null;
    this._flushing = Promise.resolve();
    this._listeners = new Set();
  }

  /**
   * Observe changes. `snapshot()` is referentially stable between writes, which
   * is exactly the contract React's useSyncExternalStore wants.
   */
  subscribe = (fn) => {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  };

  async load({ autoSeed = true } = {}) {
    const raw = await this.adapter.readAll();
    for (const k of KINDS) for (const rec of raw[k] || []) this.rows[k].set(rec.id, rec);
    for (const rec of raw.meta || []) this.meta.set(rec.id, rec.value);

    if (!this.rows.spaces.size && autoSeed) {
      const legacy = readLegacyBlob();
      const source = legacy || seed();
      this._ingest(recordsFromNested(source));
      this._setMeta('origin', legacy ? 'migrated:' + LEGACY_KEY : 'seed');
      this._setMeta('migratedAt', now());
      if (legacy) console.info('Longhand: migrated ' + LEGACY_KEY + ' into records (original left in place).');
    }
    if (!this.meta.get('deviceId')) this._setMeta('deviceId', uid());
    if (!this.meta.get('schema')) this._setMeta('schema', DB_VERSION);

    this._invalidate();
    await this.flush();
    return this;
  }

  _ingest(records) {
    for (const k of KINDS) for (const rec of records[k] || []) {
      this.rows[k].set(rec.id, rec);
      this._dirty[k].add(rec.id);
    }
    this._invalidate();
  }

  /** True when this store holds no writing at all. */
  isEmpty() { return KINDS.every(k => this.rows[k].size === 0); }

  /**
   * Plant the starter content. Called explicitly rather than on load once an
   * account is involved: only the first device may seed, and it cannot know it
   * is the first until the server has answered.
   */
  plantSeed(source = seed()) {
    if (!this.isEmpty()) return false;
    this._ingest(recordsFromNested(source));
    this._setMeta('origin', 'seed');
    return true;
  }

  /** Adopt records wholesale — writing from before sign-in, say. */
  adopt(records) {
    this._ingest(records);
    this._setMeta('origin', 'adopted');
    return KINDS.reduce((a, k) => a + (records[k]?.length || 0), 0);
  }

  /** One record by kind and id, tombstones included. */
  get(kind, id) { return this.rows[kind]?.get(id) || null; }

  /** Records whose local edits have not reached the server. Stage 2 pushes these. */
  pending() {
    const out = {};
    for (const k of KINDS) out[k] = [...this.rows[k].values()]
      .filter(r => !r.syncedAt || r.updatedAt > r.syncedAt);
    return out;
  }

  /**
   * Record what the server now holds. `syncedAt` is deliberately a *local*
   * stamp — the record's own updatedAt at the moment it was pushed — so the
   * dirty check never compares this device's clock against the server's.
   */
  markSynced(kind, id, at) {
    const r = this.rows[kind].get(id);
    if (!r) return null;
    this.rows[kind].set(id, { ...r, syncedAt: at || r.updatedAt });
    this._dirty[kind].add(id);
    this._schedule();
    return this.rows[kind].get(id);
  }

  _setMeta(key, value) {
    this.meta.set(key, value);
    this._dirty.meta.add(key);
    this._schedule();
  }

  getMeta(key) { return this.meta.get(key); }
  setMeta(key, value) { this._setMeta(key, value); }

  _invalidate() {
    this._projection = null;
    for (const fn of this._listeners) fn();
  }

  _schedule() {
    if (this._timer) return;
    this._timer = setTimeout(() => { this._timer = null; this.flush(); }, WRITE_DEBOUNCE_MS);
  }

  /** Force pending writes to disk. Safe to call often; resolves when settled. */
  flush() {
    if (this._timer) { clearTimeout(this._timer); this._timer = null; }
    const batch = { spaces: [], documents: [], notes: [], meta: [] };
    let any = false;
    for (const k of KINDS) {
      for (const id of this._dirty[k]) { const r = this.rows[k].get(id); if (r) { batch[k].push(r); any = true; } }
      this._dirty[k].clear();
    }
    for (const key of this._dirty.meta) { batch.meta.push({ id: key, value: this.meta.get(key) }); any = true; }
    this._dirty.meta.clear();
    if (!any) return this._flushing;
    this._flushing = this._flushing
      .then(() => this.adapter.write(batch))
      .catch(e => console.error('Longhand: write failed —', e));
    return this._flushing;
  }

  _touch(kind, rec) {
    rec.updatedAt = now();
    this.rows[kind].set(rec.id, rec);
    this._dirty[kind].add(rec.id);
    this._invalidate();
    this._schedule();
    return rec;
  }

  _nextPosition(kind, spaceId) {
    let max = -1;
    for (const r of this.rows[kind].values()) {
      if (kind !== 'spaces' && r.spaceId !== spaceId) continue;
      if (r.position > max) max = r.position;
    }
    return max + 1;
  }

  /* ---- reads ---- */

  /** All records including tombstones — what export and (later) sync consume. */
  records() {
    const out = {};
    for (const k of KINDS) out[k] = [...this.rows[k].values()];
    return out;
  }

  /** Nested, tombstone-free view for rendering. */
  snapshot = () => {
    if (this._projection) return this._projection;
    const live = k => [...this.rows[k].values()].filter(r => !r.deletedAt)
      .sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
    const docs = live('documents'), notes = live('notes');
    this._projection = {
      spaces: live('spaces').map(sp => ({
        ...sp,
        docs: docs.filter(d => d.spaceId === sp.id),
        notes: notes.filter(n => n.spaceId === sp.id)
      }))
    };
    return this._projection;
  };

  /* ---- writes ---- */

  patchSpace(id, patch) { const r = this.rows.spaces.get(id); return r ? this._touch('spaces', { ...r, ...patch }) : null; }
  patchDocument(id, patch) { const r = this.rows.documents.get(id); return r ? this._touch('documents', { ...r, ...patch }) : null; }
  patchNote(id, patch) { const r = this.rows.notes.get(id); return r ? this._touch('notes', { ...r, ...patch }) : null; }

  addSpace(fields = {}) {
    return this._touch('spaces', {
      id: uid(), name: '', kind: '', description: '', goal: 0,
      position: this._nextPosition('spaces'), deletedAt: null, ...fields
    });
  }

  /**
   * Tombstone a space and everything inside it.
   *
   * The cascade is the point. A document whose space is gone is still a live
   * record: it would sync forever, count toward totals, and turn up in exports
   * filed under a space that no longer exists.
   */
  removeSpace(id) {
    const space = this.rows.spaces.get(id);
    if (!space || space.deletedAt) return null;
    const at = now();

    for (const kind of ['documents', 'notes']) {
      // Snapshot first: _touch writes back into the map being read.
      for (const rec of [...this.rows[kind].values()]) {
        if (rec.spaceId === id && !rec.deletedAt) this._touch(kind, { ...rec, deletedAt: at });
      }
    }
    return this._touch('spaces', { ...space, deletedAt: at });
  }

  addDocument(spaceId, fields = {}) {
    return this._touch('documents', {
      id: uid(), spaceId, title: '', synopsis: '', content: '',
      position: this._nextPosition('documents', spaceId), deletedAt: null, ...fields
    });
  }
  addNote(spaceId, kind, fields = {}) {
    return this._touch('notes', {
      id: uid(), spaceId, kind, title: '', body: '',
      position: this._nextPosition('notes', spaceId), deletedAt: null, ...fields
    });
  }

  /**
   * Move a document to `index` among the other live documents in its space.
   *
   * Only the moved record changes: it takes a position halfway between its new
   * neighbours. Renumbering the whole space would restamp every document, and
   * each of those is a record another device may be editing — a drag should not
   * be able to turn into a row of conflict copies. Positions are real for this
   * reason. Only when there is no room between two neighbours (they share a
   * position, or halving has run out of precision) is the space renumbered.
   */
  moveDocument(id, index) {
    const doc = this.rows.documents.get(id);
    if (!doc || doc.deletedAt) return null;
    const live = [...this.rows.documents.values()]
      .filter(d => d.spaceId === doc.spaceId && !d.deletedAt)
      .sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
    const others = live.filter(d => d.id !== id);
    const to = Math.max(0, Math.min(index, others.length));
    if (live.indexOf(doc) === to) return doc;

    const before = others[to - 1], after = others[to];
    const position = !before ? after.position - 1
      : !after ? before.position + 1
      : (before.position + after.position) / 2;
    if ((!before || position > before.position) && (!after || position < after.position)) {
      return this.patchDocument(id, { position });
    }

    others.splice(to, 0, doc);
    others.forEach((d, n) => { if (d.position !== n) this.patchDocument(d.id, { position: n }); });
    return this.rows.documents.get(id);
  }

  removeDocument(id) { return this.patchDocument(id, { deletedAt: now() }); }
  removeNote(id) { return this.patchNote(id, { deletedAt: now() }); }

  /** Set a record verbatim, marking it for the next flush. */
  _accept(kind, rec) {
    this.rows[kind].set(rec.id, rec);
    this._dirty[kind].add(rec.id);
  }

  /**
   * Keep the version that is about to be discarded, as a sibling record.
   * Losing a merge should cost you a duplicate to tidy up, never your words.
   *
   * `loser` is whichever side is being thrown away — that is not always the
   * local one. `originId` is the record they both descend from.
   */
  _conflictCopy(kind, loser, originId, stamp) {
    const copy = {
      ...loser,
      id: uid(),
      title: (loser.title || 'Untitled') + ' (conflict ' + stamp.slice(0, 10) + ')',
      // Half-steps keep the copy next to the record it came from without
      // renumbering anything; positions are only ever compared, never counted.
      position: (Number(loser.position) || 0) + 0.5,
      conflictOf: originId,
      conflictAt: stamp,
      syncedAt: undefined,
      updatedAt: stamp,
      deletedAt: null
    };
    this._accept(kind, copy);
    return copy;
  }

  /**
   * Last-write-wins merge, with a copy kept whenever the losing side held
   * writing the winner never saw — in either direction. The conflict policy
   * lives in exactly one place on purpose; import and sync both come here.
   *
   * The direction matters. When the incoming record wins, the local one is
   * about to be overwritten. When the *local* record wins, the incoming one is
   * about to be discarded — and during a sync that is another device's writing,
   * which this device will then overwrite on its next push. Both are losses.
   *
   * `syncedAt` is what the server last received from us, so it marks the last
   * point the two sides agreed. Only with it can we tell "they edited while we
   * edited" from "we are simply ahead", which is why the symmetric case is
   * limited to synced records. An import has no such baseline, and treating
   * every older record in a backup as a conflict would bury the desk in
   * duplicates.
   */
  merge(records, { conflictCopies = true } = {}) {
    const report = { created: 0, updated: 0, skipped: 0, conflicts: 0 };
    const stamp = now();

    for (const k of KINDS) for (const incoming of records[k] || []) {
      if (!incoming || !incoming.id) continue;
      const mine = this.rows[k].get(incoming.id);

      if (!mine) {
        this._accept(k, incoming);
        report.created++;
        continue;
      }
      if (!((incoming.updatedAt || '') > (mine.updatedAt || ''))) {
        // We win. If the other side also moved since we last agreed, their
        // version is about to be discarded — and our next push will overwrite
        // it on the server too. Keep it.
        if (conflictCopies && CONFLICTABLE.has(k) && mine.syncedAt && !incoming.deletedAt
            && (incoming.updatedAt || '') > mine.syncedAt
            && (mine.updatedAt || '') > mine.syncedAt
            && differs(k, mine, incoming)
            && hasWriting(k, incoming)) {
          this._conflictCopy(k, incoming, incoming.id, stamp);
          report.conflicts++;
        }
        report.skipped++;
        continue;
      }

      // The incoming record wins. Would anything of ours be lost by taking it?
      if (conflictCopies && CONFLICTABLE.has(k) && !mine.deletedAt) {
        const localChanged = mine.syncedAt ? mine.updatedAt > mine.syncedAt : true;
        // A remote delete landing on local writing loses the most; treat it the
        // same as a divergent edit. A remote *edit* over a local delete simply
        // resurrects the record, which costs nothing.
        const wouldLose = incoming.deletedAt ? hasWriting(k, mine) : differs(k, mine, incoming);
        if (localChanged && wouldLose) {
          this._conflictCopy(k, mine, mine.id, stamp);
          report.conflicts++;
        }
      }

      this._accept(k, { ...mine, ...incoming });
      report.updated++;
    }

    this._invalidate();
    this._schedule();
    return report;
  }

  /** Release the underlying connection. */
  async close() {
    await this.flush();
    this.adapter.close?.();
  }

  async replaceAll(records) {
    await this.adapter.wipe();
    for (const k of KINDS) this.rows[k].clear();
    this._ingest(records);
    this._setMeta('deviceId', this.meta.get('deviceId') || uid());
    return this.flush();
  }
}

export async function openStore({ name = DB_NAME, autoSeed = true } = {}) {
  const store = new Store(await pickAdapter(name));
  store.name = name;
  await store.load({ autoSeed });

  // Never lose the last few hundred milliseconds of typing to a closed tab.
  const flush = () => store.flush();
  addEventListener('pagehide', flush);
  addEventListener('beforeunload', flush);
  addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });
  saveBeforeQuit(flush);

  return store;
}
